import { useEffect, useMemo, useState } from 'react';
import CardDropdown from './CardDropdown';
import {
  clearErrors,
  fetchActiveAlertsRequest,
  fetchAlertContactPreferencesRequest,
  fetchEmailOptionsRequest,
  fetchPhoneOptionsRequest,
  resetSmsVerificationState,
  RootState,
  sendVerificationTextRequest,
  updateAlertContactPreferencesRequest,
  updateEmailForAlertsRequest,
  updatePhoneForAlertsRequest,
  useRecaptchaToken,
} from '@rb-serve-app/core';
import { useSelector, useDispatch } from 'react-redux';
import ManageAlertsModals from './ManageAlertsModals';
import Spinner from './Spinner';

type PhoneTypeKey = 'ALT1' | 'ALT2' | 'HOME' | 'BUSINESS';
type MappedType = 'ALTERNATE_1' | 'ALTERNATE_2' | 'HOME' | 'BUSINESS';
type Phone = {
  type: PhoneTypeKey;
  verificationStatus?: string;
};
type ApiPhone = {
  type: PhoneTypeKey | MappedType;
  value: string;
  verificationStatus?: string;
};
type MappedPhone = {
  type: string;
  number: string;
  rawNumber: string;
  apiType: string;
  normalizedNumber: string;
  verificationStatus: string;
  isSelected: boolean;
  label: any;
};
interface ChangeAlertPreferencesProps {
  rb_manage_alerts: any;
  assets: any;
  onClose: () => void;
  route: any;
  alertContactPrefs: any;
  contactPrefs: any;
}

const ChangeAlertPreferences: React.FC<ChangeAlertPreferencesProps> = ({
  rb_manage_alerts,
  assets,
  onClose,
  route,
  contactPrefs,
  alertContactPrefs,
}) => {
  const dispatch = useDispatch();
  const {
    contactPrefsLoading,
    phoneOptionsLoading,
    phoneOptions,
    emailOptionsLoading,
    emailOptions,
    emailUpdateLoading,
    phoneUpdateLoading,
    contactPrefsUpdateLoading,
    error,
  } = useSelector((state: RootState) => state.manageAlerts);
  const { success, error: smsVerificationError } = useSelector(
    (state: RootState) => state.smsVerification
  );
  const accountDetails = useSelector(
    (state: RootState) => state.user.accountDetails
  );
  const PHONE_TYPE_MAPPING = rb_manage_alerts?.phoneTypeMapping as Record<
    MappedType,
    {
      label: any;
      type: PhoneTypeKey;
    }
  >;
  const getRecaptchaToken = useRecaptchaToken();
  const API_PHONE_TYPE_MAPPING: Record<PhoneTypeKey, MappedType> = {
    ALT1: 'ALTERNATE_1',
    ALT2: 'ALTERNATE_2',
    HOME: 'HOME',
    BUSINESS: 'BUSINESS',
  };

  const ALL_PHONE_TYPES = rb_manage_alerts?.manageAlertsAllPhoneTypes;
  const {
    availableCards,
    selectedCard,
    isAlertsModalOpen,
    setIsAlertsModalOpen,
    setSelectedCard,
  } = route?.params || {};
  const [currentSelectedCard, setCurrentSelectedCard] = useState(selectedCard);
  const [isEmailEditing, setIsEmailEditing] = useState(false);
  const [isPhoneEditing, setIsPhoneEditing] = useState(false);
  const [didEmailUpdate, setDidEmailUpdate] = useState(false);
  const [didPhoneUpdate, setDidPhoneUpdate] = useState(false);
  const [email, setEmail] = useState(contactPrefs?.emailAddress || '');
  const [editingEmail, setEditingEmail] = useState(email);
  const [isEmailAvailable, setIsEmailAvailable] = useState(
    !!contactPrefs?.emailAddress
  );
  const [phoneNumbers, setPhoneNumbers] = useState<MappedPhone[]>([]);
  const [editingPhoneNumbers, setEditingPhoneNumbers] = useState<
    Record<string, string>
  >({});
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<string[]>(
    []
  );
  const [phoneVerificationStatus, setPhoneVerificationStatus] = useState<
    Record<string, string>
  >({});
  const [originalEmail, setOriginalEmail] = useState('');
  const [originalPhoneNumbers, setOriginalPhoneNumbers] = useState<
    MappedPhone[]
  >([]);
  const [originalSelectedPhoneNumbers, setOriginalSelectedPhoneNumbers] =
    useState<string[]>([]);
  const [dataFullyLoaded, setDataFullyLoaded] = useState(false);
  const [showPhoneSpinner, setShowPhoneSpinner] = useState(false);
  const [showEmailSpinner, setShowEmailSpinner] = useState(false);
  const [refreshingEmail, setRefreshingEmail] = useState(false);
  const [refreshingPhone, setRefreshingPhone] = useState(false);
  const [contactPrefsUpdating, setContactPrefsUpdating] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneErrors, setPhoneErrors] = useState<Record<string, boolean>>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [showSuccessModalPhone, setShowSuccessModalPhone] = useState(false);
  const [showErrorModalPhone, setShowErrorModalPhone] = useState(false);
  // Add state for phone verification modal
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] =
    useState(false);

  // Derive whether the user is adding phone numbers from current vs original selection.
  const isAddingPhoneNumbers = useMemo(
    () => selectedPhoneNumbers.length > originalSelectedPhoneNumbers.length,
    [selectedPhoneNumbers, originalSelectedPhoneNumbers]
  );

  useEffect(() => {
    if (route?.params?.selectedCard) {
      setCurrentSelectedCard(route.params.selectedCard);
    }
  }, [route?.params?.selectedCard]);

  useEffect(() => {
    setDidEmailUpdate(false);
    setDidPhoneUpdate(false);
    dispatch(clearErrors());
  }, []);

  useEffect(() => {
    if (alertContactPrefs) {
      if (
        currentSelectedCard?.customerType === 'AUTHORIZED_USER' &&
        alertContactPrefs.emailAddress
      ) {
        setEmail(alertContactPrefs.emailAddress);
        setEditingEmail(alertContactPrefs.emailAddress);
        setOriginalEmail(alertContactPrefs.emailAddress);
        setIsEmailAvailable(true);
      }

      if (
        alertContactPrefs.programs &&
        Array.isArray(alertContactPrefs.programs)
      ) {
        const selectedTypes: string[] = [];
        const verificationStatuses: Record<string, string> = {};

        alertContactPrefs.programs.forEach((program: any) => {
          if (program.selectedPhones && Array.isArray(program.selectedPhones)) {
            program.selectedPhones.forEach((phone: Phone) => {
              const mappedApiType = API_PHONE_TYPE_MAPPING[phone.type];

              const mappedPhone = PHONE_TYPE_MAPPING?.[mappedApiType];
              if (mappedApiType && mappedPhone) {
                selectedTypes.push(mappedPhone.type);
                verificationStatuses[mappedPhone.type] =
                  phone.verificationStatus || '';
              }
            });
          }
        });

        setSelectedPhoneNumbers(selectedTypes);
        setPhoneVerificationStatus(verificationStatuses);
      } else {
        setSelectedPhoneNumbers([]);
        setPhoneVerificationStatus({});
      }
    } else {
      setSelectedPhoneNumbers([]);
      setPhoneVerificationStatus({});
    }
  }, [alertContactPrefs, currentSelectedCard]);

  const formatPhoneNumber = (input: string) => {
    if (!input) return '';
    let cleaned = input;
    if (cleaned.startsWith('+1')) {
      cleaned = cleaned.substring(2);
    }
    cleaned = cleaned.replace(/\D/g, '');
    const digits = cleaned.slice(0, 10);
    if (digits.length >= 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length > 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length > 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length > 0) {
      return `(${digits}`;
    }
    return '';
  };

  const normalizePhoneNumber = (input: string) => {
    if (!input) return '';
    return input.replace(/^\+1/, '').replace(/\D/g, '');
  };

  useEffect(() => {
    if (!contactPrefs) return;
    if (contactPrefs.emailAddress) {
      setEmail(contactPrefs.emailAddress);
      setEditingEmail(contactPrefs.emailAddress);
      setOriginalEmail(contactPrefs.emailAddress);
      setIsEmailAvailable(true);
    } else if (alertContactPrefs?.emailAddress) {
      setEmail(alertContactPrefs.emailAddress);
      setEditingEmail(alertContactPrefs.emailAddress);
      setOriginalEmail(alertContactPrefs.emailAddress);
      setIsEmailAvailable(true);
    } else {
      setEmail('');
      setEditingEmail('');
      setOriginalEmail('');
      setIsEmailAvailable(false);
    }

    const apiPhoneNumbers: MappedPhone[] = [];
    const phoneNumbersMap: Record<string, string> = {};
    const verificationStatuses: Record<string, string> = {};

    const getSelectedPhones = (): ApiPhone[] => {
      if (
        alertContactPrefs?.programs &&
        Array.isArray(alertContactPrefs.programs)
      ) {
        return alertContactPrefs.programs.flatMap((program: any) =>
          Array.isArray(program.selectedPhones) ? program.selectedPhones : []
        );
      }
      return [];
    };

    const selectedPhones = getSelectedPhones();

    const processPhones = (phones: ApiPhone[], useApiMapping = false) => {
      phones.forEach((phone) => {
        const mappedTypeKey = useApiMapping
          ? API_PHONE_TYPE_MAPPING[
              phone.type as keyof typeof API_PHONE_TYPE_MAPPING
            ]
          : phone.type;

        const mappedPhoneMeta =
          PHONE_TYPE_MAPPING?.[
            mappedTypeKey as keyof typeof PHONE_TYPE_MAPPING
          ];

        if (!mappedPhoneMeta) return;

        const formattedNumber = formatPhoneNumber(phone.value);
        const normalizedNumber = normalizePhoneNumber(phone.value);

        const matchingSelectedPhone = selectedPhones.find(
          (selectedPhone) =>
            normalizePhoneNumber(selectedPhone.value) === normalizedNumber
        );

        const verificationStatus =
          matchingSelectedPhone?.verificationStatus || '';
        const isSelected =
          !!matchingSelectedPhone ||
          selectedPhoneNumbers.includes(mappedPhoneMeta.type);
        const label = mappedPhoneMeta.label;
        const mappedPhone: MappedPhone = {
          ...mappedPhoneMeta,
          number: formattedNumber,
          rawNumber: phone.value,
          apiType: phone.type,
          normalizedNumber,
          verificationStatus,
          isSelected,
          label,
        };

        if (phone.value) {
          apiPhoneNumbers.push(mappedPhone);
          phoneNumbersMap[mappedPhone.type] = formattedNumber;
          if (verificationStatus) {
            verificationStatuses[mappedPhone.type] = verificationStatus;
          }
        }
      });
    };

    if (
      Array.isArray(contactPrefs.phoneNumber) &&
      contactPrefs.phoneNumber.length > 0
    ) {
      processPhones(contactPrefs.phoneNumber);
    } else if (
      alertContactPrefs?.programs?.[0]?.phones &&
      Array.isArray(alertContactPrefs.programs[0].phones)
    ) {
      processPhones(alertContactPrefs.programs[0].phones, true);
    }

    const selectedTypes = apiPhoneNumbers
      .filter((phone) => phone.isSelected)
      .map((phone) => phone.type);

    setPhoneNumbers(apiPhoneNumbers);
    setEditingPhoneNumbers(phoneNumbersMap);
    setSelectedPhoneNumbers(selectedTypes);
    setOriginalPhoneNumbers([...apiPhoneNumbers]);
    setOriginalSelectedPhoneNumbers([...selectedTypes]);
    setPhoneVerificationStatus(verificationStatuses);

    if (alertContactPrefs) {
      setDataFullyLoaded(true);
    }
  }, [contactPrefs, alertContactPrefs, currentSelectedCard]);

  useEffect(() => {
    if (!emailUpdateLoading && didEmailUpdate) {
      setRefreshingEmail(true);
      if (accountDetails) {
        const accountId = accountDetails.accountId;
        const customerId = currentSelectedCard
          ? currentSelectedCard?.customerId
          : accountDetails.customer?.customerId;

        if (accountId && customerId) {
          if (currentSelectedCard?.customerType === 'AUTHORIZED_USER') {
            setEmail(editingEmail);
            setOriginalEmail(editingEmail);
          }

          dispatch(
            fetchActiveAlertsRequest({
              accountId,
              customerId,
            })
          );

          dispatch(
            fetchAlertContactPreferencesRequest({
              accountId,
              customerId,
            })
          );
        }
      }
      if (isAddingPhoneNumbers) {
        setShowPhoneVerificationModal(true);
      } else {
        setShowSuccessModal(true);

        setTimeout(() => {
          setShowSuccessModal(false);
          setIsAlertsModalOpen(false);
        }, 2000);
      }

      setDidEmailUpdate(false);
    }
  }, [
    emailUpdateLoading,
    currentSelectedCard,
    didEmailUpdate,
    editingEmail,
    accountDetails,
    dispatch,
    isAddingPhoneNumbers,
  ]);

  useEffect(() => {
    if (isEmailEditing || isPhoneEditing) {
      if (isEmailEditing) setDidEmailUpdate(false);

      if (isPhoneEditing) setDidPhoneUpdate(false);
    }
  }, [isEmailEditing, isPhoneEditing]);

  const validateInput = (type: string, value: string) => {
    if (type === 'email') {
      if (value.trim() === '') {
        return 'Email address cannot be empty';
      } else if (!isValidEmail(value)) {
        return 'Please enter a valid email address';
      }
      return '';
    } else if (type === 'phone') {
      if (value === '') {
        return '';
      }
      const digits = value.replace(/\D/g, '');
      if (digits.length !== 10) {
        return 'Phone number must be 10 digits';
      }
      return '';
    }
    return '';
  };

  const hasChanges = useMemo(() => {
    if (contactPrefsLoading || !dataFullyLoaded) return false;

    if (
      (isEmailEditing && emailError) ||
      (isPhoneEditing && Object.values(phoneErrors).some(Boolean))
    ) {
      return false;
    }

    const hasEmailChanged = isEmailEditing && editingEmail !== originalEmail;

    const hasPhoneEditsChanged = (): boolean => {
      for (const phone of originalPhoneNumbers) {
        const originalNumber = phone.number;
        const editingNumber = normalizePhoneNumber(
          editingPhoneNumbers[phone.type] || ''
        );
        const normalizedOriginalNumber = normalizePhoneNumber(originalNumber);
        if (normalizedOriginalNumber !== editingNumber) return true;
      }

      for (const type in editingPhoneNumbers) {
        if (
          editingPhoneNumbers[type] &&
          !originalPhoneNumbers.some((p) => p.type === type)
        ) {
          if (normalizePhoneNumber(editingPhoneNumbers[type])) {
            return true;
          }
        }
      }
      return false;
    };

    const haveSelectedPhoneNumbersChanged = (): boolean => {
      if (selectedPhoneNumbers.length !== originalSelectedPhoneNumbers.length)
        return true;
      const sortedOriginal = [...originalSelectedPhoneNumbers].sort();
      const sortedCurrent = [...selectedPhoneNumbers].sort();
      return sortedOriginal.some((val, i) => val !== sortedCurrent[i]);
    };

    const havePhoneNumbersChanged = (): boolean => {
      if (phoneNumbers.length !== originalPhoneNumbers.length) return true;

      const mapByType = (phones: typeof phoneNumbers): Record<string, string> =>
        phones.reduce(
          (acc, p) => {
            acc[p.type] = normalizePhoneNumber(p.number);
            return acc;
          },
          {} as Record<string, string>
        );

      const currentMap = mapByType(phoneNumbers);
      const originalMap = mapByType(originalPhoneNumbers);

      const allTypes = new Set([
        ...Object.keys(currentMap),
        ...Object.keys(originalMap),
      ]);
      for (const type of allTypes) {
        if (currentMap[type] !== originalMap[type]) return true;
      }

      return false;
    };

    return (
      hasEmailChanged ||
      (isPhoneEditing && hasPhoneEditsChanged()) ||
      haveSelectedPhoneNumbersChanged() ||
      havePhoneNumbersChanged()
    );
  }, [
    contactPrefsLoading,
    dataFullyLoaded,
    isEmailEditing,
    isPhoneEditing,
    email,
    editingEmail,
    originalEmail,
    selectedPhoneNumbers,
    originalSelectedPhoneNumbers,
    phoneNumbers,
    originalPhoneNumbers,
    editingPhoneNumbers,
    emailError,
    phoneErrors,
    isEmailAvailable,
    currentSelectedCard,
  ]);

  useEffect(() => {
    if (!phoneOptionsLoading && showPhoneSpinner) {
      setShowPhoneSpinner(false);
      setIsPhoneEditing(true);
    }
  }, [phoneOptionsLoading, showPhoneSpinner]);

  useEffect(() => {
    if (!emailOptionsLoading && showEmailSpinner) {
      setShowEmailSpinner(false);
      setIsEmailEditing(true);
    }
  }, [emailOptionsLoading, showEmailSpinner]);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleEmailEdit = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEditingEmail(value);
    const error = validateInput('email', value);
    setEmailError(error);
  };

  const handlePhoneEdit = (value: string, type: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    const formattedNumber = formatPhoneNumber(digitsOnly);
    setEditingPhoneNumbers((prev) => ({
      ...prev,
      [type]: formattedNumber,
    }));
    const isEmpty =
      value.trim() === '' || digitsOnly === '' || formattedNumber === '';
    const isValid = isEmpty || digitsOnly.length === 10;
    setPhoneErrors((prev) => {
      const newErrors = {
        ...prev,
        [type]: !isValid,
      };

      return newErrors;
    });
  };

  const handlePhoneSelection = (type: string) => {
    setSelectedPhoneNumbers((prev) => {
      const newSelectedPhoneNumbers = prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type];
      setPhoneNumbers((prev) =>
        prev.map((phone) => {
          if (phone.type === type) {
            return {
              ...phone,
              isSelected: !phone.isSelected,
            };
          }
          return phone;
        })
      );

      return newSelectedPhoneNumbers;
    });
  };

  const handleEmailEditStart = () => {
    setShowEmailSpinner(true);
    setEditingEmail(email);
    setEmailError('');
    if (accountDetails) {
      const accountId = accountDetails.accountId;
      const customerId = currentSelectedCard
        ? currentSelectedCard?.customerId
        : accountDetails.customer?.customerId;

      if (accountId && customerId) {
        dispatch(
          fetchEmailOptionsRequest({
            accountId,
            customerId,
          })
        );
      }
    }
  };

  const handlePhoneEditStart = () => {
    setPhoneErrors({});
    setShowPhoneSpinner(true);
    setDidPhoneUpdate(false);
    if (accountDetails) {
      const accountId = accountDetails.accountId;
      const customerId = currentSelectedCard
        ? currentSelectedCard?.customerId
        : accountDetails.customer?.customerId;

      if (accountId && customerId) {
        dispatch(
          fetchPhoneOptionsRequest({
            accountId,
            customerId,
          })
        );
      }
    }

    const currentPhoneNumbers = { ...editingPhoneNumbers };

    ALL_PHONE_TYPES.forEach((phone: any) => {
      if (!currentPhoneNumbers[phone.type]) {
        currentPhoneNumbers[phone.type] = '';
      }
    });

    setEditingPhoneNumbers(currentPhoneNumbers);
  };

  const handleUpdate = async () => {
    if (isEmailEditing) {
      const emailError = validateInput('email', editingEmail);
      const token = await getRecaptchaToken('change_alert_Preference_email');
      if (emailError) {
        setEmailError(emailError);
        return;
      }
      setRefreshingEmail(true);
      if (emailOptions && accountDetails) {
        const accountId = accountDetails.accountId;
        const customerId = currentSelectedCard
          ? currentSelectedCard?.customerId
          : accountDetails.customer?.customerId;

        const requestBody = {
          assignmentId: emailOptions.assignmentId,
          emailAddress: editingEmail,
          recaptchaToken: token,
        };

        dispatch(
          updateEmailForAlertsRequest({
            accountId,
            customerId,
            requestBody,
          })
        );

        setDidEmailUpdate(true);

        if (currentSelectedCard?.customerType === 'AUTHORIZED_USER') {
          setEmail(editingEmail);
          setOriginalEmail(editingEmail);
        }
      }

      setEmail(editingEmail);
      setIsEmailEditing(false);

      return;
    }

    if (isPhoneEditing) {
      const token = await getRecaptchaToken('change_alert_Preference_phones');
      setRefreshingPhone(true);
      const updatedPhoneNumbers: any = [];
      Object.entries(editingPhoneNumbers).forEach(([type, number]) => {
        if (number) {
          const phoneType = ALL_PHONE_TYPES.find((p: any) => p.type === type);
          if (phoneType) {
            const rawNumber = String(number).replace(/\D/g, '');
            updatedPhoneNumbers.push({
              id: phoneType.id,
              type: phoneType.type,
              label: phoneType.label,
              number: number,
              rawNumber: rawNumber,
              apiType: phoneType.apiType,
              normalizedNumber: normalizePhoneNumber(rawNumber),
              verificationStatus: phoneVerificationStatus[type],
              isSelected: selectedPhoneNumbers.includes(type),
            });
          }
        }
      });

      setPhoneNumbers(updatedPhoneNumbers);
      setIsPhoneEditing(false);

      if (phoneOptions && accountDetails) {
        const phonesForAPI: any = [];

        Object.entries(editingPhoneNumbers).forEach(([type, number]) => {
          const phoneType = ALL_PHONE_TYPES.find((p: any) => p.type === type);
          if (phoneType) {
            const originalPhone = originalPhoneNumbers.find(
              (p) => p.type === type
            );
            const normalizedNewNumber = normalizePhoneNumber(
              String(number || '')
            );
            const wasEdited =
              !originalPhone ||
              (originalPhone &&
                normalizedNewNumber !== originalPhone.normalizedNumber) ||
              (originalPhone && originalPhone.normalizedNumber && !number);
            if (wasEdited) {
              const rawNumber = number ? String(number).replace(/\D/g, '') : '';
              phonesForAPI.push({
                type: phoneType.apiType,
                number: rawNumber,
              });
            }
          }
        });
        if (phonesForAPI.length > 0) {
          const accountId = accountDetails.accountId;
          const customerId = currentSelectedCard
            ? currentSelectedCard?.customerId
            : accountDetails.customer?.customerId;

          if (accountId && customerId) {
            const requestBody = {
              phones: phonesForAPI,
              assignmentId: phoneOptions.assignmentId,
              recaptchaToken: token,
            };

            dispatch(
              updatePhoneForAlertsRequest({
                accountId,
                customerId,
                requestBody,
              })
            );
            setDidPhoneUpdate(true);
          }
        } else {
          setRefreshingPhone(false);
        }
      }
    }

    if (!isEmailEditing && !isPhoneEditing) {
      const token = await getRecaptchaToken('change_alert_contact_prefrence');
      setContactPrefsUpdating(true);
      if (accountDetails && contactPrefs) {
        const allPhones = (contactPrefs.phoneNumber || []).map((phone: any) => {
          // Apply the specific mapping for update_alert_contact_preferences
          let mappedType = phone.type;
          if (mappedType === 'ALTERNATE_1') mappedType = 'ALT1';
          if (mappedType === 'ALTERNATE_2') mappedType = 'ALT2';

          return {
            type: mappedType,
            value: phone.value || '',
          };
        });

        const updatedSelectedPhones = phoneNumbers
          .filter((phone) => {
            return selectedPhoneNumbers.includes(phone.type);
          })
          .map((phone) => {
            const apiType = ALL_PHONE_TYPES.find(
              (p: any) => p.type === phone.type
            )?.apiType;

            let mappedType = apiType;
            if (apiType === 'ALTERNATE_1') mappedType = 'ALT1';
            if (apiType === 'ALTERNATE_2') mappedType = 'ALT2';

            return {
              type: mappedType,
              verificationStatus: phoneVerificationStatus[phone.type] || '',
              value: phone.rawNumber.startsWith('+1')
                ? phone.rawNumber
                : `+1${phone.rawNumber}`,
            };
          });

        const contactPrefsRequestBody = {
          customerIdentifiers: {
            accountId: accountDetails.accountId,
            customerId: currentSelectedCard
              ? currentSelectedCard?.customerId
              : accountDetails.customer?.customerId,
          },
          emailAddress: email,
          recaptchaToken: token,
          programs: [
            {
              programName: 'PROGRAM_1',
              phones: allPhones,
              selectedPhones: updatedSelectedPhones,
            },
          ],
        };

        dispatch(
          updateAlertContactPreferencesRequest({
            requestBody: contactPrefsRequestBody,
          })
        );
      }
    }
  };

  useEffect(() => {
    if (!phoneUpdateLoading && didPhoneUpdate) {
      setRefreshingPhone(true);
      // setIsAlertsModalOpen(false);
      if (isAddingPhoneNumbers) {
        setShowPhoneVerificationModal(true);
      } else {
        setShowSuccessModal(true);

        setTimeout(() => {
          setShowSuccessModal(false);
          setIsAlertsModalOpen(false);
        }, 2000);
      }

      if (accountDetails) {
        const accountId = accountDetails.accountId;
        const customerId = currentSelectedCard
          ? currentSelectedCard?.customerId
          : accountDetails.customer?.customerId;

        if (accountId && customerId) {
          dispatch(
            fetchActiveAlertsRequest({
              accountId,
              customerId,
            })
          );
        }
      }

      setDidPhoneUpdate(false);
    }
  }, [phoneUpdateLoading, currentSelectedCard, didPhoneUpdate]);

  useEffect(() => {
    if (contactPrefs && !contactPrefsLoading) {
      setRefreshingEmail(false);
      setRefreshingPhone(false);
    }
  }, [contactPrefs, contactPrefsLoading]);

  useEffect(() => {
    if (!contactPrefsUpdateLoading && contactPrefsUpdating) {
      setContactPrefsUpdating(false);

      // If the user added phone numbers we want to show phone verification modal
      // instead of the generic success modal. Guard here so other effects
      // don't overwrite the intended modal.
      if (isAddingPhoneNumbers) {
        setShowPhoneVerificationModal(true);
      } else {
        setShowSuccessModal(true);

        setTimeout(() => {
          setShowSuccessModal(false);
          setIsAlertsModalOpen(false);
        }, 2000);
      }

      if (accountDetails) {
        const accountId = accountDetails.accountId;
        const customerId = currentSelectedCard
          ? currentSelectedCard?.customerId
          : accountDetails.customer?.customerId;

        if (accountId && customerId) {
          setOriginalEmail(email);
          setOriginalPhoneNumbers([...phoneNumbers]);
          setOriginalSelectedPhoneNumbers([...selectedPhoneNumbers]);
        }
      }
    }
  }, [
    contactPrefsUpdateLoading,
    contactPrefsUpdating,
    accountDetails,
    dispatch,
    email,
    phoneNumbers,
    selectedPhoneNumbers,
    currentSelectedCard,
  ]);

  useEffect(() => {
    setIsEmailEditing(false);
    setIsPhoneEditing(false);
    setShowEmailSpinner(false);
    setShowPhoneSpinner(false);
    setEmailError('');

    setPhoneVerificationStatus({});
    setPhoneNumbers([]);
    setSelectedPhoneNumbers([]);
    setDataFullyLoaded(false);
    if (currentSelectedCard && accountDetails) {
      const accountId = accountDetails.accountId;
      const customerId = currentSelectedCard?.customerId;

      if (accountId && customerId) {
        dispatch(
          fetchAlertContactPreferencesRequest({
            accountId,
            customerId,
          })
        );
      }
    }
  }, [currentSelectedCard]);

  const handleCardSelectionChange = (card: any) => {
    setIsEmailEditing(false);
    setIsPhoneEditing(false);
    setShowEmailSpinner(false);
    setShowPhoneSpinner(false);
    setDidEmailUpdate(false);
    setDidPhoneUpdate(false);
    setPhoneVerificationStatus({});
    setPhoneNumbers([]);
    setCurrentSelectedCard(card);
    setDataFullyLoaded(false);
    setSelectedCard(card);
  };

  const handleCancel = () => {
    if (
      hasChanges ||
      (emailError && isEmailEditing) ||
      (phoneErrors && isPhoneEditing)
    ) {
      setShowUnsavedChangesModal(true);
    } else {
      onClose();
    }
  };

  const revertChanges = () => {
    // Revert email changes
    setEditingEmail(originalEmail);
    setEmail(originalEmail);
    setIsEmailEditing(false);
    setEmailError('');

    // Revert phone changes
    const originalPhoneNumbersMap: Record<string, string> = {};
    originalPhoneNumbers.forEach((phone) => {
      originalPhoneNumbersMap[phone.type] = phone.number;
    });
    setEditingPhoneNumbers(originalPhoneNumbersMap);
    setPhoneNumbers([...originalPhoneNumbers]);
    setSelectedPhoneNumbers([...originalSelectedPhoneNumbers]);
    setIsPhoneEditing(false);
    setPhoneErrors({});

    // Reset verification status
    const originalVerificationStatuses: Record<string, string> = {};
    originalPhoneNumbers.forEach((phone) => {
      if (phone.verificationStatus) {
        originalVerificationStatuses[phone.type] = phone.verificationStatus;
      }
    });
    setPhoneVerificationStatus(originalVerificationStatuses);
  };

  useEffect(() => {
    if (success) {
      setRefreshingPhone(true);
      setTimeout(() => {
        setRefreshingPhone(false);
        setShowSuccessModalPhone(true);
        setTimeout(() => {
          setShowSuccessModalPhone(false);
          setIsAlertsModalOpen(false);
        }, 2000);
        if (accountDetails) {
          const accountId = accountDetails.accountId;
          const customerId =
            currentSelectedCard?.customerId ||
            accountDetails.customer?.customerId;

          if (accountId && customerId) {
            dispatch(
              fetchAlertContactPreferencesRequest({
                accountId,
                customerId,
              })
            );
          }
        }
      }, 2000);
    }
    if (smsVerificationError) {
      setShowErrorModalPhone(true);
    }
    dispatch(resetSmsVerificationState());
  }, [
    success,
    accountDetails,
    currentSelectedCard,
    dispatch,
    smsVerificationError,
  ]);

  const handlePhoneVerification = async (
    phoneNumber: string,
    phoneType: 'mobile' | 'home' | 'business' | 'other'
  ) => {
    if (!accountDetails || !phoneNumber || !phoneType) {
      setShowErrorModalPhone(true);
      return;
    }

    let apiPhoneType: 'ALT1' | 'HOME' | 'BUSINESS' | 'ALT2' = 'BUSINESS';
    switch (phoneType) {
      case 'mobile':
        apiPhoneType = 'ALT1';
        break;
      case 'home':
        apiPhoneType = 'HOME';
        break;
      case 'business':
        apiPhoneType = 'BUSINESS';
        break;
      case 'other':
        apiPhoneType = 'ALT2';
        break;
    }

    const formattedPhoneNumber = formatPhoneNumberForApi(phoneNumber);

    const payload = {
      customerIdentifiers: {
        accountId: accountDetails.accountId,
        customerId: currentSelectedCard
          ? currentSelectedCard?.customerId
          : accountDetails.customer?.customerId,
      },
      phoneInfo: {
        type: apiPhoneType,
        value: formattedPhoneNumber,
        verificationStatus: 'SENT',
      },
      programName: 'PROGRAM_1',
    };

    dispatch(sendVerificationTextRequest(payload));
  };

  const formatPhoneNumberForApi = (phone: string): string => {
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length === 10) {
      return `+1${digitsOnly}`;
    }
    if (digitsOnly.length > 10) {
      return `+${digitsOnly}`;
    }
    return `+1${digitsOnly}`;
  };

  return (
    <div className="flex flex-col h-full rounded-lg bg-white overflow-hidden">
      <div className="flex flex-row bg-white px-4 py-6 md:rounded-t-lg w-full border-b shrink-0">
        <p className="flex-1 font-[650] text-[22px]">
          {rb_manage_alerts?.webAlertsHeader?.webAlertPrefTitle}
        </p>
        <div onClick={handleCancel} className="close-btn cursor-pointer pr-2">
          <img
            src={assets.popup_close_icon}
            className="w-6 h-6 p-0 m-0"
            alt="PopupCloseIcon"
          />
        </div>
      </div>
      <div className="overflow-y-auto flex-1">
        {availableCards?.length > 1 && (
          <div className="px-6 py-4">
            <label className="text-base text-[#1F1F1F] font-semibold">
              {rb_manage_alerts?.manageAlertsTextSendNotificationsTo}
            </label>
            <div className="flex justify-between items-center pt-3 relative text-base w-full">
              <CardDropdown
                customStyle={true}
                className="w-full min-w-[180px] text-sm md:text-base font-normal px-2 lg:pt-2 text-[#1F1F1F] border border-[#ccc] cursor-pointer"
                availableCards={availableCards}
                selectedCard={currentSelectedCard}
                setSelectedCard={handleCardSelectionChange}
              />
            </div>
          </div>
        )}

        <div className="pb-4">
          <div className="flex justify-between items-center p-2.5 px-6 mb-2.5 py-2.5">
            <label className="text-base text-[#1F1F1F] font-semibold">
              {rb_manage_alerts?.manageAlertsPreferredEmailAddresss}
            </label>
            {!isEmailEditing && !isPhoneEditing && (
              <button
                onClick={handleEmailEditStart}
                className="flex items-center text-red-700 text-sm tracking-tight disabled:text-gray-400"
              >
                {showEmailSpinner ? (
                  <div className="ml-2 w-4 h-4 border-t-2 border-red-700 rounded-full animate-spin" />
                ) : (
                  <img
                    src={assets.icon_edit_alerts}
                    alt="edit"
                    className="w-6 h-6 ml-1"
                  />
                )}
              </button>
            )}
          </div>

          {contactPrefsLoading || refreshingEmail ? (
            <div className="px-6 py-4">
              <Spinner />
            </div>
          ) : isEmailEditing && !error ? (
            <div className="px-6">
              <div className="flex items-center flex-1 relative">
                <input
                  type="email"
                  value={editingEmail}
                  onChange={handleEmailEdit}
                  placeholder="Enter email address"
                  className="w-full border px-3 py-2 rounded bg-white"
                />
                {editingEmail && (
                  <img
                    src={assets.popup_close_icon}
                    alt="clearIcon"
                    className="absolute right-3 h-6 w-6 cursor-pointer"
                    onClick={() => setEditingEmail('')}
                  />
                )}
              </div>
              {!!emailError && (
                <p className="text-sm text-red-600 mt-1">
                  {rb_manage_alerts?.manageAlertsHeader?.inlineErrEmail}
                </p>
              )}
            </div>
          ) : (
            <div className="flex justify-between items-center gap-3">
              {isEmailAvailable ? (
                <span className="text-base text-[#1F1F1F] px-6 space-y-3">
                  {email}
                </span>
              ) : (
                <div className="flex flex-col items-center justify-center w-full py-4">
                  <img
                    src={assets.icon_no_email_found}
                    alt="not found"
                    className="w-20 h-20 mb-2"
                  />
                  <p className="text-sm text-gray-600">
                    {rb_manage_alerts?.manageAlertsEmailNotFound}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="pb-4">
          <div className="flex justify-between items-center px-6 py-2.5 mb-2.5">
            <label className="text-base text-[#1F1F1F] font-semibold">
              {rb_manage_alerts?.manageAlertsPhoneNumbers}
            </label>
            {!isPhoneEditing && !isEmailEditing && (
              <button
                onClick={handlePhoneEditStart}
                className="flex items-center"
              >
                {showPhoneSpinner ? (
                  <div className="ml-2 w-4 h-4 border-t-2 border-red-700 rounded-full animate-spin" />
                ) : (
                  <img
                    src={assets?.icon_edit_alerts}
                    alt="Edit"
                    className="w-6 h-6 ml-1"
                  />
                )}
              </button>
            )}
          </div>

          {contactPrefsLoading || refreshingPhone ? (
            <div className="px-6 py-4 space-y-2">
              <Spinner />
            </div>
          ) : (
            <div className="px-6 space-y-3">
              {isPhoneEditing && !error ? (
                ALL_PHONE_TYPES.map((item: any) => (
                  <div key={item.type} className="space-y-1">
                    <label className="text-sm text-[#1F1F1F]">
                      {item.label}
                    </label>
                    <div className="flex items-center flex-1 relative">
                      <input
                        type="tel"
                        placeholder={
                          rb_manage_alerts?.manageAlertsHeader
                            ?.placeHolderPhoneNumber
                        }
                        maxLength={14}
                        value={editingPhoneNumbers[item.type] || ''}
                        onChange={(e) =>
                          handlePhoneEdit(e.target.value, item.type)
                        }
                        className="w-full border px-3 py-2 rounded bg-white"
                      />
                      {editingPhoneNumbers && (
                        <img
                          src={assets.popup_close_icon}
                          alt="clearIcon"
                          className="absolute right-3 h-6 w-6 cursor-pointer"
                          onClick={() =>
                            setEditingPhoneNumbers((prev) => ({
                              ...prev,
                              [item.type]: '',
                            }))
                          }
                        />
                      )}
                    </div>
                    {item.type === 'mobile' && phoneErrors[item.type] && (
                      <div className="text-sm text-red-600 mt-1">
                        {rb_manage_alerts?.manageAlertsHeader?.inlineErr}
                      </div>
                    )}
                  </div>
                ))
              ) : phoneNumbers.length > 0 ? (
                phoneNumbers.map((item) => (
                  <div key={item.type} className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={
                        selectedPhoneNumbers.includes(item.type) ||
                        item.isSelected
                      }
                      disabled={isEmailEditing}
                      onChange={() => handlePhoneSelection(item.type)}
                      className="h-5 w-5 mt-2 appearance-none bg-white border border-gray-300 rounded-sm checked:bg-red-600 focus:ring-red-500 cursor-pointer
               relative checked:after:absolute checked:after:content-['✓'] checked:after:text-white  checked:after:text-sm checked:after:left-[4px] checked:after:top-[-2px] font-bold "
                    />

                    <div className="flex justify-between items-start w-full">
                      <div className="flex flex-col flex-1">
                        <label className="text-base text-[#1F1F1F] mb-0.5 block">
                          {item.label}
                        </label>
                        <div className="text-base text-[#1F1F1F] font-normal">
                          {item.number}
                        </div>
                      </div>

                      <div className="text-base text-gray-600 mt-1 flex items-center min-w-[120px] justify-end">
                        {item.verificationStatus === 'VERIFIED' && (
                          <span className="text-[#666]">
                            {
                              rb_manage_alerts?.manageAlertsPhoneStatus
                                ?.verified
                            }
                          </span>
                        )}
                        {['SENT', 'EXPIRED', 'FAILED'].includes(
                          item.verificationStatus
                        ) && (
                          <div
                            className="flex items-center gap-1 text-[#DA291C] cursor-pointer"
                            onClick={() => {
                              if (
                                [
                                  'mobile',
                                  'home',
                                  'business',
                                  'other',
                                ].includes(item.type as string)
                              ) {
                                handlePhoneVerification(
                                  item.number,
                                  item.type as
                                    | 'mobile'
                                    | 'home'
                                    | 'business'
                                    | 'other'
                                );
                              }
                            }}
                          >
                            <span>
                              {item.verificationStatus === 'SENT'
                                ? rb_manage_alerts?.manageAlertsPhoneStatus
                                    ?.sent
                                : item.verificationStatus === 'EXPIRED'
                                  ? rb_manage_alerts?.manageAlertsPhoneStatus
                                      ?.expired
                                  : rb_manage_alerts?.manageAlertsPhoneStatus
                                      ?.failed}
                            </span>
                            <img
                              src={
                                item.verificationStatus === 'SENT'
                                  ? assets?.icon_message_sent
                                  : item.verificationStatus === 'EXPIRED'
                                    ? assets?.icon_expired
                                    : assets?.error_icon
                              }
                              alt={item.verificationStatus.toLowerCase()}
                              className="w-4 h-4"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 flex flex-col items-center">
                  <img
                    src={assets?.icon_no_phone_found}
                    alt="No phones"
                    className="w-20 h-20 mb-2"
                  />
                  <p className="text-sm text-gray-500">
                    {rb_manage_alerts?.webAlertsHeader?.webNoMobilePrefText}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bg-[#fafafa] flex gap-[15px] text-[16px] font-[650] leading-[20px] tracking-[-0.051px] p-[15px] md:px-[30px] md:py-[20px] lg:p-[30px] shrink-0">
        <button
          type="button"
          onClick={handleCancel}
          className="text-center flex-1 flex-grow items-center align-center border border-solid border-[#DA291C] text-[#DA291C] px-[20px] py-[12px] bg-[#fff]"
        >
          {rb_manage_alerts?.webAlertsHeader?.cancelCta}
        </button>
        <button
          type="submit"
          onClick={handleUpdate}
          className={`text-center flex-1 flex-grow items-center align-center border border-solid px-[20px] py-[12px] ${hasChanges ? 'border-[#DA291C] text-[#ffffff] bg-[#DA291C]' : 'border-[#D9D9D9] text-[#999999] bg-[#F5F5F5]'}`}
        >
          {rb_manage_alerts?.webAlertsHeader?.updateCta}
        </button>
      </div>

      <ManageAlertsModals
        rb_manage_alerts={rb_manage_alerts}
        assets={assets}
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowSuccessModal}
        showUnsavedChangesModal={showUnsavedChangesModal}
        setShowUnsavedChangesModal={setShowUnsavedChangesModal}
        isAlertsModalOpen={isAlertsModalOpen}
        setIsAlertsModalOpen={setIsAlertsModalOpen}
        showErrorModalPhone={showErrorModalPhone}
        setShowErrorModalPhone={setShowErrorModalPhone}
        showSuccessModalPhone={showSuccessModalPhone}
        setShowSuccessModalPhone={setShowSuccessModalPhone}
        revertChanges={revertChanges}
        setShowPhoneVerificationModal={setShowPhoneVerificationModal}
        showPhoneVerificationModal={showPhoneVerificationModal}
      />
    </div>
  );
};

export default ChangeAlertPreferences;
